import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ImageSquare, Sparkle, PencilSimple, Camera, Cloud, Sun, SpeakerHigh,
  Quotes, Brain, FilmReel, ArrowsClockwise,
} from "@/components/icons";
import { Download, Upload, Film } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VideoGenModal from "@/components/VideoGenModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CAMERA = ["Wide Cinematic Shot", "Establishing Shot", "Medium Shot", "Close-Up", "Extreme Close-Up", "Low Angle", "High Angle", "Bird's Eye View", "Over-the-Shoulder", "Dutch Angle", "Tracking Shot"];
const EMOTION = ["Joyful", "Calm", "Excited", "Anxious", "Sad", "Quiet Devastation", "Angry", "Fearful", "Hopeful", "Determined", "Shocked"];
const WEATHER = ["Clear", "Calm evening", "Cloudy", "Rain", "Heavy Storm", "Snow", "Foggy", "Windy"];
const TIME = ["Dawn", "Morning", "Midday", "Late afternoon", "Golden Hour", "Dusk", "Night", "Midnight"];

function uniq(list, val) {
  return val && !list.includes(val) ? [val, ...list] : list;
}

function QuickSelect({ icon: Icon, value, options, onChange, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-auto bg-ink-950 border-zinc-800 text-xs py-2 px-2.5 focus:ring-gold focus:border-gold rounded-none" data-testid={testid}>
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={14} className="text-gold shrink-0" />
          <SelectValue placeholder="—" />
        </div>
      </SelectTrigger>
      <SelectContent className="bg-ink-900 border-zinc-800 text-white max-h-72">
        {uniq(options, value).map((o) => (
          <SelectItem key={o} value={o} className="text-xs focus:bg-ink-800 focus:text-gold">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function PanelCard({ panel, index, projectId, onUpdate }) {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [p, setP] = useState(panel);
  const [genLoading, setGenLoading] = useState(false);
  const [dirLoading, setDirLoading] = useState(false);
  const [command, setCommand] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(panel);
  const [animOpen, setAnimOpen] = useState(false);
  const [animRefs, setAnimRefs] = useState([]);
  const [animBusy, setAnimBusy] = useState(false);

  useEffect(() => { setP(panel); }, [panel]);

  const patch = async (field, value) => {
    setP((prev) => ({ ...prev, [field]: value }));
    try {
      const { data } = await api.put(`/projects/${projectId}/panels/${panel.id}`, { [field]: value });
      onUpdate(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveDraft = async () => {
    try {
      const { data } = await api.put(`/projects/${projectId}/panels/${panel.id}`, {
        scene_title: draft.scene_title, description: draft.description, dialogue: draft.dialogue,
        character_thoughts: draft.character_thoughts, character_poses: draft.character_poses,
        lighting: draft.lighting, animation_notes: draft.animation_notes,
        sound_effects: typeof draft.sound_effects === "string" ? draft.sound_effects.split(",").map((s) => s.trim()).filter(Boolean) : draft.sound_effects,
      });
      onUpdate(data);
      setEditOpen(false);
      toast.success("Panel updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generateImage = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/panels/${panel.id}/generate-image`);
      setP((prev) => ({ ...prev, image_base64: data.image_base64, image_status: "done" }));
      const proj = await api.get(`/projects/${projectId}`);
      onUpdate(proj.data);
      await refreshUser();
      toast.success(`Frame ${index + 1} generated · -5 credits`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Out of credits — upgrade or buy a pack to keep generating.");
        navigate("/pricing");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Image generation failed");
      }
    } finally { setGenLoading(false); }
  };

  const downloadImage = () => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${p.image_base64}`;
    a.download = `panel_${String(index + 1).padStart(2, "0")}.png`;
    a.click();
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/panels/${panel.id}/upload-image`, { image_base64: reader.result });
        setP((prev) => ({ ...prev, image_base64: data.image_base64, image_status: "done" }));
        const proj = await api.get(`/projects/${projectId}`);
        onUpdate(proj.data);
        toast.success("Image uploaded");
      } catch (err) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const runDirector = async () => {
    if (!command.trim()) return;
    setDirLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/panels/${panel.id}/director`, { command });
      onUpdate(data);
      setCommand("");
      toast.success("Director applied changes");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDirLoading(false); }
  };

  const openEdit = () => { setDraft({ ...p, sound_effects: (p.sound_effects || []).join(", ") }); setEditOpen(true); };

  const animate = async () => {
    if (!p.image_base64) { toast.error("Generate this panel's image first"); return; }
    setAnimBusy(true);
    try {
      const dataUrl = `data:image/png;base64,${p.image_base64}`;
      const { data } = await api.post(`/uploads/image`, { image_base64: dataUrl, origin_url: window.location.origin });
      setAnimRefs([{ url: data.url }]);
      setAnimOpen(true);
    } catch (e) { toast.error("Could not prepare image for animation"); }
    finally { setAnimBusy(false); }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-ink-900 border border-zinc-800 flex flex-col"
      data-testid={`panel-${index}`}
    >
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-ink-800/50">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-xs text-ink-950 bg-gold px-1.5 py-0.5 font-bold">{String(index + 1).padStart(2, "0")}</span>
          <span className="font-heading font-semibold text-sm truncate">{p.scene_title}</span>
        </div>
        <button onClick={openEdit} className="text-zinc-500 hover:text-gold transition-colors shrink-0" data-testid={`panel-edit-${index}`}>
          <PencilSimple size={16} />
        </button>
      </div>

      {/* image */}
      <div className="relative aspect-video bg-ink-950 overflow-hidden border-b border-zinc-800">
        {genLoading && <div className="absolute top-0 left-0 right-0 h-0.5 tracing-border z-10" />}
        <input ref={fileRef} type="file" accept="image/*" onChange={onUploadFile} className="hidden" data-testid={`upload-input-${index}`} />
        {p.image_base64 ? (
          <img src={`data:image/png;base64,${p.image_base64}`} alt={p.scene_title} className="w-full h-full object-cover" data-testid={`panel-image-${index}`} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <ImageSquare size={32} weight="duotone" className="text-zinc-700" />
            <button
              onClick={generateImage}
              disabled={genLoading}
              data-testid={`generate-image-${index}`}
              className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold text-sm px-4 py-2 hover:bg-goldlight transition-colors disabled:opacity-60"
            >
              <Sparkle weight="fill" size={16} /> {genLoading ? "Rendering…" : "Generate Image · 5"}
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid={`upload-btn-${index}`} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-gold transition-colors">
              <Upload size={13} /> or upload your own
            </button>
          </div>
        )}
        {p.image_base64 && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
            <button onClick={animate} disabled={animBusy} data-testid={`animate-${index}`} className="flex items-center gap-1 bg-gold text-ink-950 text-xs px-2 py-1.5 font-heading font-bold hover:bg-goldlight transition-colors" title="Animate this panel">{animBusy ? "…" : <><Film size={13} /> Animate</>}</button>
            <button onClick={downloadImage} data-testid={`download-image-${index}`} className="flex items-center gap-1 bg-ink-950/80 backdrop-blur text-white text-xs px-2 py-1.5 border border-zinc-700 hover:border-gold transition-colors" title="Download PNG"><Download size={13} /></button>
            <button onClick={() => fileRef.current?.click()} data-testid={`upload-btn-${index}`} className="flex items-center gap-1 bg-ink-950/80 backdrop-blur text-white text-xs px-2 py-1.5 border border-zinc-700 hover:border-gold transition-colors" title="Upload"><Upload size={13} /></button>
            <button onClick={generateImage} disabled={genLoading} data-testid={`regenerate-image-${index}`} className="flex items-center gap-1 bg-ink-950/80 backdrop-blur text-white text-xs px-2.5 py-1.5 border border-zinc-700 hover:border-gold transition-colors" title="Regenerate · 5 credits"><ArrowsClockwise size={13} /> {genLoading ? "…" : "5"}</button>
          </div>
        )}
      </div>

      {/* director's notes */}
      <div className="p-3 space-y-3 flex-1 flex flex-col">
        <p className="text-sm text-zinc-300 leading-relaxed">{p.description}</p>

        <div className="grid grid-cols-2 gap-2">
          <QuickSelect icon={Camera} value={p.camera_angle} options={CAMERA} onChange={(v) => patch("camera_angle", v)} testid={`panel-camera-${index}`} />
          <QuickSelect icon={Sparkle} value={p.emotion} options={EMOTION} onChange={(v) => patch("emotion", v)} testid={`panel-emotion-${index}`} />
          <QuickSelect icon={Sun} value={p.time_of_day} options={TIME} onChange={(v) => patch("time_of_day", v)} testid={`panel-time-${index}`} />
          <QuickSelect icon={Cloud} value={p.weather} options={WEATHER} onChange={(v) => patch("weather", v)} testid={`panel-weather-${index}`} />
        </div>

        {p.dialogue && (
          <div className="bg-ink-950 border-l-2 border-gold px-3 py-2">
            <div className="flex items-center gap-1.5 label-mono mb-1"><Quotes size={12} /> Dialogue</div>
            <p className="text-sm text-white italic">"{p.dialogue}"</p>
          </div>
        )}
        {p.character_thoughts && (
          <div className="flex items-start gap-2 text-zinc-400 text-sm">
            <Brain size={15} className="text-cyanx mt-0.5 shrink-0" />
            <span className="italic">{p.character_thoughts}</span>
          </div>
        )}
        {p.animation_notes && (
          <div>
            <div className="flex items-center gap-1.5 label-mono mb-1"><FilmReel size={12} /> Animation</div>
            <p className="text-xs text-zinc-400 font-mono leading-relaxed">{p.animation_notes}</p>
          </div>
        )}
        {(p.sound_effects || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {p.sound_effects.map((s, i) => (
              <span key={i} className="flex items-center gap-1 text-xs bg-ink-800 border border-zinc-800 px-2 py-1 text-zinc-300">
                <SpeakerHigh size={11} className="text-gold" /> {s}
              </span>
            ))}
          </div>
        )}

        {/* AI director */}
        <div className="mt-auto pt-2 flex gap-2">
          <input
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runDirector()}
            placeholder="Direct: 'make it rain', 'zoom in'…"
            data-testid={`director-input-${index}`}
            className="flex-1 bg-ink-950 border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-gold focus:outline-none min-w-0"
          />
          <button
            onClick={runDirector}
            disabled={dirLoading}
            data-testid={`director-send-${index}`}
            className="bg-cyanx/20 border border-cyanx/40 text-cyanx px-3 hover:bg-cyanx/30 transition-colors disabled:opacity-50 shrink-0"
          >
            {dirLoading ? <span className="text-xs">…</span> : <Sparkle weight="fill" size={15} />}
          </button>
        </div>
      </div>

      {/* edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-ink-900 border-zinc-800 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-black uppercase tracking-tight">Edit Panel {index + 1}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { k: "scene_title", l: "Scene Title", rows: 1 },
              { k: "description", l: "What's Happening", rows: 3 },
              { k: "dialogue", l: "Dialogue", rows: 2 },
              { k: "character_thoughts", l: "Character Thoughts", rows: 2 },
              { k: "character_poses", l: "Character Poses & Direction", rows: 2 },
              { k: "lighting", l: "Lighting", rows: 1 },
              { k: "animation_notes", l: "Animation Notes", rows: 2 },
              { k: "sound_effects", l: "Sound Effects (comma separated)", rows: 1 },
            ].map((f) => (
              <div key={f.k}>
                <label className="label-mono block mb-1.5">{f.l}</label>
                <textarea
                  rows={f.rows}
                  value={draft[f.k] || ""}
                  onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                  data-testid={`edit-${f.k}-${index}`}
                  className="w-full bg-ink-950 border border-zinc-800 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-none"
                />
              </div>
            ))}
            <button onClick={saveDraft} data-testid={`save-panel-${index}`} className="w-full bg-gold text-ink-950 font-heading font-bold py-3 hover:bg-goldlight transition-colors">Save Panel</button>
          </div>
        </DialogContent>
      </Dialog>

      <VideoGenModal open={animOpen} onOpenChange={setAnimOpen} projectId={projectId} projectTitle={`Panel ${index + 1}`} defaultPrompt={p.animation_notes} defaultRefs={animRefs} onDone={() => toast.success("Animating panel — see the Animation section below")} />
    </motion.div>
  );
}
